import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { DASHBOARD_URL, VALIDATION_PATTERNS } from "../../constants/app";
import { sendOtp, verifyOTP } from "../../service/Auth.services";
import OTPModal from "components/Modals/SignInOtpModal";
import ShowToast from "components/ShowToast";
import APP_CONFIG from "../../constants/app";

/* ─────────────────────────────────────────────
   Scoped styles — Fintech Animated Theme
───────────────────────────────────────────── */
const THEME_MAP = {
  1: { hex: "#00d4aa", hover: "#00e8bb", rgb: "0,212,170", bg: "#060b18" },
  2: { hex: "#4f46e5", hover: "#6366f1", rgb: "79,70,229", bg: "#020617" },
  3: { hex: "#f59e0b", hover: "#fbbf24", rgb: "245,158,11", bg: "#171717" },
  4: { hex: "#0ea5e9", hover: "#38bdf8", rgb: "14,165,233", bg: "#0f172a" },
  5: { hex: "#ec4899", hover: "#f472b6", rgb: "236,72,153", bg: "#1e1b4b" }
};

const getStyles = (hex, hover, rgb, bg) => `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  /* ── Reset ── */
  .ft-page *, .ft-page *::before, .ft-page *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* ── Root ── */
  .ft-page {
    min-height: 100vh;
    display: flex;
    font-family: 'DM Sans', sans-serif;
    background: ${bg};
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
  }

  /* ── Canvas background (animated) ── */
  .ft-canvas {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }

  /* ── Floating particles ── */
  .ft-particles {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    overflow: hidden;
  }
  .ft-particle {
    position: absolute;
    border-radius: 50%;
    opacity: 0;
    animation: ft-particle-float linear infinite;
  }
  @keyframes ft-particle-float {
    0%   { opacity: 0;    transform: translateY(100vh) scale(0); }
    10%  { opacity: 0.6; }
    90%  { opacity: 0.3; }
    100% { opacity: 0;    transform: translateY(-10vh) scale(1); }
  }

  /* ── Grid overlay ── */
  .ft-grid {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    background-image:
      linear-gradient(rgba(,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(,0.04) 1px, transparent 1px);
    background-size: 48px 48px;
  }

  /* ── Orbs ── */
  .ft-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    z-index: 1;
    animation: ft-orb-drift ease-in-out infinite alternate;
  }
  .ft-orb-1 {
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(,0.12) 0%, transparent 70%);
    top: -100px; left: -100px;
    animation-duration: 10s;
  }
  .ft-orb-2 {
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%);
    bottom: -80px; right: 300px;
    animation-duration: 14s;
    animation-delay: -3s;
  }
  .ft-orb-3 {
    width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%);
    top: 40%; right: -60px;
    animation-duration: 9s;
    animation-delay: -6s;
  }
  @keyframes ft-orb-drift {
    from { transform: translate(0, 0) scale(1); }
    to   { transform: translate(20px, 30px) scale(1.08); }
  }

  /* ── Ticker strip ── */
  .ft-ticker-wrap {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
    height: 32px;
    background: rgba(,0.05);
    border-top: 1px solid rgba(,0.12);
    overflow: hidden;
    display: flex;
    align-items: center;
  }
  .ft-ticker {
    display: flex;
    gap: 0;
    animation: ft-ticker-scroll 28s linear infinite;
    white-space: nowrap;
  }
  @keyframes ft-ticker-scroll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  .ft-ticker-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 28px;
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: rgba(${rgb},0.55);
    letter-spacing: 0.04em;
    border-right: 1px solid rgba(${rgb},0.1);
  }
  .ft-ticker-item .up   { color: ${hex}; }
  .ft-ticker-item .down { color: #f43f5e; }

  /* ── Left panel ── */
  .ft-left {
    flex: 1;
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 60px 48px;
    animation: ft-fade-in 0.8s ease both;
  }
  @keyframes ft-fade-in {
    from { opacity: 0; transform: translateX(-24px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .ft-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: clamp(16px, 4vh, 64px);
    animation: ft-drop 0.6s ease both;
  }
  .ft-logo img {
    max-height: 48px;
    object-fit: contain;
    background: #fff;
    padding: 6px 14px;
    border-radius: 8px;
  }
  .ft-logo-text {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    font-weight: 500;
    color: rgba(255,255,255,0.6);
    letter-spacing: 0.06em;
  }

  .ft-headline {
    margin-bottom: clamp(12px, 3vh, 24px);
    animation: ft-drop 0.6s 0.1s ease both;
  }
  .ft-headline-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: ${hex};
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: clamp(8px, 2vh, 16px);
    padding: 4px 10px;
    border: 1px solid rgba(${rgb},0.3);
    border-radius: 4px;
    background: rgba(${rgb},0.06);
  }
  .ft-headline-tag::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: ${hex};
    box-shadow: 0 0 8px ${hex};
    animation: ft-blink 1.8s ease-in-out infinite;
  }
  @keyframes ft-blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
  .ft-headline h1 {
    font-size: clamp(2rem, 3.5vw, 2.8rem);
    font-weight: 700;
    color: #fff;
    line-height: 1.12;
    letter-spacing: -0.02em;
  }
  .ft-headline h1 .accent {
    color: ${hex};
    position: relative;
  }

  .ft-desc {
    font-size: 14px;
    color: rgba(255,255,255,0.4);
    line-height: 1.7;
    max-width: 380px;
    margin-bottom: clamp(16px, 4vh, 48px);
    animation: ft-drop 0.6s 0.2s ease both;
  }

  /* ── Stats ── */
  .ft-stats {
    display: flex;
    gap: 0;
    animation: ft-drop 0.6s 0.3s ease both;
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 12px;
    background: rgba(255,255,255,0.03);
    overflow: hidden;
    max-width: 420px;
  }
  .ft-stat {
    flex: 1;
    padding: 20px 24px;
    border-right: 1px solid rgba(255,255,255,0.07);
  }
  .ft-stat:last-child { border-right: none; }
  .ft-stat-val {
    font-family: 'DM Mono', monospace;
    font-size: 22px;
    font-weight: 500;
    color: ${hex};
    margin-bottom: 4px;
    display: block;
  }
  .ft-stat-lbl {
    font-size: 10px;
    color: rgba(255,255,255,0.35);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  /* ── Card visual ── */
  .ft-card-visual {
    margin-top: 48px;
    animation: ft-drop 0.6s 0.4s ease both;
    position: relative;
    max-width: 320px;
  }
  .ft-card-chip {
    background: linear-gradient(135deg, #1a2a1a 0%, #0d1a0d 100%);
    border: 1px solid rgba(${rgb},0.2);
    border-radius: 16px;
    padding: 24px 28px;
    position: relative;
    overflow: hidden;
  }
  .ft-card-chip::before {
    content: '';
    position: absolute;
    top: -40px; right: -40px;
    width: 120px; height: 120px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(${rgb},0.15) 0%, transparent 70%);
  }
  .ft-card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }
  .ft-card-brand {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: rgba(${rgb},0.7);
    letter-spacing: 0.08em;
  }
  .ft-card-chip-icon {
    width: 32px; height: 24px;
    background: linear-gradient(135deg, #c8a97e, #e8c99e);
    border-radius: 4px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2px;
    padding: 4px;
    overflow: hidden;
  }
  .ft-chip-cell {
    background: rgba(0,0,0,0.2);
    border-radius: 1px;
  }
  .ft-card-num {
    font-family: 'DM Mono', monospace;
    font-size: 14px;
    color: rgba(255,255,255,0.7);
    letter-spacing: 0.15em;
    margin-bottom: 16px;
    display: flex;
    gap: 12px;
  }
  .ft-card-bottom {
    display: flex;
    justify-content: space-between;
  }
  .ft-card-holder {
    font-size: 11px;
    color: rgba(255,255,255,0.4);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .ft-card-expiry {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: rgba(${rgb},0.6);
  }

  /* ── Right panel (form) ── */
  .ft-right {
    width: 460px;
    flex-shrink: 0;
    position: relative;
    z-index: 3;
    background: rgba(10,17,30,0.92);
    border-left: 1px solid rgba(255,255,255,0.07);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 44px;
    animation: ft-slide-right 0.6s ease both;
    backdrop-filter: blur(20px);
  }
  @keyframes ft-slide-right {
    from { opacity: 0; transform: translateX(30px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .ft-form-inner { width: 100%; }

  /* ── Form header ── */
  .ft-form-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 36px;
  }
  .ft-form-brand img {
    max-height: 42px;
    object-fit: contain;
    background: #fff;
    padding: 6px 14px;
    border-radius: 8px;
  }

  .ft-form-hd { margin-bottom: 32px; }
  .ft-form-pre {
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    color: ${hex};
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ft-form-pre::before {
    content: '';
    display: block;
    width: 24px; height: 1px;
    background: ${hex};
  }
  .ft-form-title {
    font-size: 26px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 6px;
    letter-spacing: -0.02em;
  }
  .ft-form-sub {
    font-size: 12px;
    color: rgba(255,255,255,0.35);
    font-weight: 400;
    line-height: 1.6;
  }

  /* ── Alert ── */
  .ft-alert {
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    animation: ft-alert-in 0.3s ease both;
  }
  @keyframes ft-alert-in {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .ft-alert-danger {
    background: rgba(244,63,94,0.08);
    color: #fb7185;
    border: 1px solid rgba(244,63,94,0.25);
  }

  /* ── Fields ── */
  .ft-field { margin-bottom: 18px; }
  .ft-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255,255,255,0.45);
    margin-bottom: 8px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .ft-input-wrap { position: relative; }
  .ft-input-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 15px;
    pointer-events: none;
    color: rgba(255,255,255,0.25);
    transition: color 0.2s;
  }
  .ft-input {
    width: 100%;
    padding: 12px 44px 12px 42px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    color: #fff;
    background: rgba(255,255,255,0.04);
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    outline: none;
    caret-color: ${hex};
  }
  .ft-input::placeholder { color: rgba(255,255,255,0.2); }
  .ft-input:focus {
    border-color: rgba(${rgb},0.5);
    background: rgba(${rgb},0.04);
    box-shadow: 0 0 0 3px rgba(${rgb},0.1);
  }
  .ft-input:focus + .ft-input-glow { opacity: 1; }
  .ft-input-glow {
    position: absolute;
    inset: 0;
    border-radius: 10px;
    pointer-events: none;
    box-shadow: 0 0 20px rgba(${rgb},0.08);
    opacity: 0;
    transition: opacity 0.3s;
  }
  .ft-input-error {
    border-color: rgba(244,63,94,0.4) !important;
    box-shadow: 0 0 0 3px rgba(244,63,94,0.08) !important;
  }
  .ft-eye-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    font-size: 15px;
    padding: 2px;
    color: rgba(255,255,255,0.25);
    transition: color 0.15s;
    line-height: 1;
  }
  .ft-eye-btn:hover { color: ${hex}; }
  .ft-error-msg {
    font-size: 11px;
    color: #fb7185;
    font-weight: 500;
    margin-top: 5px;
    display: flex;
    align-items: center;
    gap: 4px;
    animation: ft-alert-in 0.2s ease both;
  }

  /* ── Options row ── */
  .ft-opts-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 26px;
  }
  .ft-remember {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }
  .ft-remember input[type="checkbox"] {
    width: 15px; height: 15px;
    accent-color: ${hex};
    cursor: pointer;
  }
  .ft-remember-label {
    font-size: 12px;
    color: rgba(255,255,255,0.4);
    font-weight: 500;
    cursor: pointer;
    user-select: none;
  }
  .ft-forgot {
    font-size: 12px;
    font-weight: 600;
    color: ${hex};
    text-decoration: none;
    opacity: 0.8;
    transition: opacity 0.15s;
  }
  .ft-forgot:hover { opacity: 1; }

  /* ── Submit button ── */
  .ft-btn {
    width: 100%;
    padding: 13px 24px;
    border: none;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    background: ${hex};
    color: #020c08;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 24px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    transition: transform 0.15s, box-shadow 0.15s, background 0.2s;
    position: relative;
    overflow: hidden;
  }
  .ft-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }
  .ft-btn:hover:not(:disabled)::before { transform: translateX(100%); }
  .ft-btn:hover:not(:disabled) {
    background: ${hover};
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(${rgb},0.3);
  }
  .ft-btn:active:not(:disabled) { transform: translateY(0); box-shadow: none; }
  .ft-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Divider / signup ── */
  .ft-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }
  .ft-divider-line { flex: 1; height: 1px; background: rgba(255,255,255,0.07); }
  .ft-divider-text {
    font-size: 10px;
    color: rgba(255,255,255,0.2);
    font-weight: 600;
    letter-spacing: 0.1em;
  }
  .ft-signup-row {
    text-align: center;
    font-size: 12px;
    color: rgba(255,255,255,0.3);
    font-weight: 500;
  }
  .ft-signup-row a {
    color: ${hex};
    font-weight: 700;
    text-decoration: none;
    opacity: 0.9;
    transition: opacity 0.15s;
  }
  .ft-signup-row a:hover { opacity: 1; }

  /* ── Security badge ── */
  .ft-secure-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 24px;
    font-size: 10px;
    color: rgba(255,255,255,0.2);
    letter-spacing: 0.06em;
  }
  .ft-secure-badge span {
    font-family: 'DM Mono', monospace;
  }
  .ft-secure-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: ${hex};
    box-shadow: 0 0 6px ${hex};
    animation: ft-blink 2.5s ease-in-out infinite;
  }

  /* ── Toast ── */
  .ft-toast-container {
    position: fixed;
    top: 20px; right: 20px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 8px;
    pointer-events: none;
  }
  .ft-toast {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #0d1a2a;
    border-radius: 10px;
    padding: 12px 16px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.4);
    min-width: 280px;
    max-width: 360px;
    pointer-events: all;
    animation: ft-toast-in 0.35s cubic-bezier(0.34,1.56,0.64,1) both;
    position: relative;
    overflow: hidden;
  }
  .ft-toast-success { border-left: 3px solid ; }
  .ft-toast-error   { border-left: 3px solid #f43f5e; }
  .ft-toast-info    { border-left: 3px solid #3b82f6; }
  .ft-toast-out     { animation: ft-toast-out 0.3s ease forwards; }
  .ft-toast-progress {
    position: absolute;
    bottom: 0; left: 0;
    height: 2px;
    animation: ft-toast-progress 3.5s linear forwards;
  }
  .ft-toast-success .ft-toast-progress { background: ; }
  .ft-toast-error   .ft-toast-progress { background: #f43f5e; }
  .ft-toast-info    .ft-toast-progress { background: #3b82f6; }
  @keyframes ft-toast-in {
    from { opacity: 0; transform: translateX(30px) scale(0.95); }
    to   { opacity: 1; transform: translateX(0) scale(1); }
  }
  @keyframes ft-toast-out {
    from { opacity: 1; transform: translateX(0); }
    to   { opacity: 0; transform: translateX(30px); }
  }
  @keyframes ft-toast-progress { from { width: 100%; } to { width: 0%; } }
  .ft-toast-icon { font-size: 1.1rem; line-height: 1; flex-shrink: 0; margin-top: 1px; }
  .ft-toast-body { flex: 1; min-width: 0; }
  .ft-toast-title { font-size: 12px; font-weight: 700; color: #fff; margin-bottom: 2px; }
  .ft-toast-msg   { font-size: 11px; color: rgba(255,255,255,0.45); line-height: 1.4; }
  .ft-toast-close {
    background: none; border: none; cursor: pointer;
    font-size: 13px; color: rgba(255,255,255,0.3); padding: 0; line-height: 1;
    transition: color 0.15s; flex-shrink: 0; pointer-events: all;
  }
  .ft-toast-close:hover { color: #fff; }

  /* ── Drop / slide animations ── */
  @keyframes ft-drop {
    from { opacity: 0; transform: translateY(-14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Responsive ── */
  @media (max-width: 991px) {
    .ft-left { display: none; }
    .ft-right { width: 100%; border-left: none; }
  }
  @media (max-width: 480px) {
    .ft-right { padding: 36px 24px; }
  }
`;

/* ─────────────────────────────────────────────
   Animated canvas background
───────────────────────────────────────────── */
function AnimatedCanvas({ rgb }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let nodes = [];
    const NODE_COUNT = 40;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const initNodes = () => {
      nodes = Array.from({ length: NODE_COUNT }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.1,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update & draw nodes
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;

        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(,${n.opacity * 0.4})`;
        ctx.fill();
      });

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(,${0.07 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      raf = requestAnimationFrame(draw);
    };

    resize();
    initNodes();
    draw();
    window.addEventListener("resize", () => { resize(); initNodes(); });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="ft-canvas" />;
}

/* ─────────────────────────────────────────────
   Floating Particles
───────────────────────────────────────────── */
const getParticles = (rgb) => Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  size: `${Math.random() * 4 + 2}px`,
  delay: `${Math.random() * 20}s`,
  duration: `${Math.random() * 15 + 12}s`,
  color: i % 3 === 0
    ? "rgba(,0.5)"
    : i % 3 === 1
    ? "rgba(59,130,246,0.4)"
    : "rgba(139,92,246,0.4)",
}));

function Particles({ rgb }) {
  const PARTICLES = getParticles(rgb);
  return (
    <div className="ft-particles">
      {PARTICLES.map((p) => (
        <div
          key={p.id}
          className="ft-particle"
          style={{
            left: p.left,
            width: p.size,
            height: p.size,
            background: p.color,
            animationDelay: p.delay,
            animationDuration: p.duration,
          }}
        />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Ticker strip
───────────────────────────────────────────── */
const TICKERS = [
  { sym: "BTC/USD", val: "67,420.50", chg: "+2.34%", up: true },
  { sym: "ETH/USD", val: "3,841.20", chg: "+1.12%", up: true },
  { sym: "USDT",    val: "1.0002",   chg: "0.00%",  up: true },
  { sym: "BNB/USD", val: "598.30",   chg: "-0.87%", up: false },
  { sym: "SOL/USD", val: "178.45",   chg: "+3.56%", up: true },
  { sym: "XRP/USD", val: "0.6312",   chg: "-1.23%", up: false },
  { sym: "ADA/USD", val: "0.4523",   chg: "+0.94%", up: true },
  { sym: "DOGE",    val: "0.1842",   chg: "-2.11%", up: false },
];

function Ticker() {
  const items = [...TICKERS, ...TICKERS];
  return (
    <div className="ft-ticker-wrap" aria-hidden="true">
      <div className="ft-ticker">
        {items.map((t, i) => (
          <span key={i} className="ft-ticker-item">
            {t.sym}&nbsp;
            <span>{t.val}</span>&nbsp;
            <span className={t.up ? "up" : "down"}>{t.chg}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Internal Toast System
───────────────────────────────────────────── */
let _toastId = 0;
function useToasts() {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = "info", title = null) => {
    const id = ++_toastId;
    const defaultTitles = { success: "Success", error: "Error", info: "Info" };
    setToasts((prev) => [
      ...prev,
      { id, message, type, title: title || defaultTitles[type] },
    ]);
    setTimeout(() => {
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, leaving: true } : t))
      );
      setTimeout(
        () => setToasts((prev) => prev.filter((t) => t.id !== id)),
        320
      );
    }, 3500);
  };

  const removeToast = (id) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, leaving: true } : t))
    );
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      320
    );
  };

  return { toasts, addToast, removeToast };
}

const TOAST_ICONS = { success: "✅", error: "❌", info: "ℹ️" };

function ToastContainer({ toasts, removeToast }) {
  return (
    <div className="ft-toast-container">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`ft-toast ft-toast-${t.type}${t.leaving ? " ft-toast-out" : ""}`}
        >
          <span className="ft-toast-icon">{TOAST_ICONS[t.type]}</span>
          <div className="ft-toast-body">
            <div className="ft-toast-title">{t.title}</div>
            <div className="ft-toast-msg">{t.message}</div>
          </div>
          <button className="ft-toast-close" onClick={() => removeToast(t.id)}>
            ✕
          </button>
          <div className="ft-toast-progress" />
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
const SignIn = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpData, setOtpData] = useState({ phoneNumber: "", userToken: "" });
  const [showPassword, setShowPassword] = useState(false);
  const { toasts, addToast, removeToast } = useToasts();

  useEffect(() => {
    localStorage.clear();
    sessionStorage.clear();
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Phone number is required";
    } else if (!VALIDATION_PATTERNS.PHONE.test(formData.email)) {
      newErrors.email = "Phone number is invalid";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const _formData = new FormData();
      _formData.append("phone", formData.email);
      _formData.append("password", formData.password);
      const result = await sendOtp(_formData);
      if (!result.status) {
        const msg =
          result.message || "Login failed. Please check your credentials.";
        setErrors({ general: msg });
        addToast(msg, "error");
      }
      if (result.status) {
        setOtpData({
          phoneNumber: formData.email,
          userToken: result.data?.user_token || "",
        });
        const msg = result.message || "OTP sent successfully";
        ShowToast(msg, "success");
        addToast(msg, "success");
        setOtpStep(true);
      }
    } catch (error) {
      console.error("Login error:", error);
      const msg = "An unexpected error occurred. Please try again.";
      setErrors({ general: msg });
      addToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSuccess = (result) => {
    if (result.token) localStorage.setItem("authToken", result.token);
    if (result.data) {
      localStorage.setItem("userid", result.data.id);
      localStorage.setItem("name", result.data.name);
      localStorage.setItem("role", result.data.role);
      localStorage.setItem("islogin", true);
      navigate(DASHBOARD_URL[result.data.role], { replace: true });
      return;
    }
    navigate("/", { replace: true });
  };

  const handleOTPError = (error) => {
    console.error("OTP verification error:", error);
  };

  const handleResendOTP = async () => {
    try {
      const _formData = new FormData();
      _formData.append("phone", formData.email);
      _formData.append("password", formData.password);
      const result = await sendOtp(_formData);
      if (result.status) {
        setOtpData((prev) => ({
          ...prev,
          userToken: result.data?.user_token || prev.userToken,
        }));
      } else {
        throw new Error(result.data?.error || "OTP resend failed");
      }
      return result;
    } catch (error) {
      console.error("Resend OTP error:", error);
      throw error;
    }
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setOtpData({ phoneNumber: "", userToken: "" });
    setErrors({});
  };

  if (otpStep) {
    return (
      <OTPModal
        phoneNumber={otpData.phoneNumber}
        password={formData.password}
        userToken={otpData.userToken}
        otpLength={4}
        onSuccess={handleOTPSuccess}
        onError={handleOTPError}
        onResend={handleResendOTP}
        onBack={handleBackToLogin}
        verifyOTPService={verifyOTP}
        resendWaitMinutes={1}
        maxResendAttempts={3}
        autoSubmitOnComplete={false}
        title="Verify OTP"
        subtitle="Enter the 4-digit code sent to your registered phone number"
      />
    );
  }

  const curTheme = THEME_MAP[APP_CONFIG.LOGIN_THEME] || THEME_MAP[1];
  return (
    <>
      <style>{getStyles(curTheme.hex, curTheme.hover, curTheme.rgb, curTheme.bg)}</style>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="ft-page">
        {/* Animated background */}
        <AnimatedCanvas rgb={curTheme.rgb} />
        <Particles rgb={curTheme.rgb} />
        <div className="ft-grid" aria-hidden="true" />
        <div className="ft-orb ft-orb-1" aria-hidden="true" />
        <div className="ft-orb ft-orb-2" aria-hidden="true" />
        <div className="ft-orb ft-orb-3" aria-hidden="true" />

        {/* ── Left panel ── */}
        <div className="ft-left">
          <div className="ft-logo">
            <img src={APP_CONFIG.ORGANISATION_LOGO} alt="Logo" />
          </div>

          <div className="ft-headline">
            <div className="ft-headline-tag">Live platform</div>
            <h1>
              Next-gen <span className="accent">fintech</span>
              <br />infrastructure
              <br />for retailers
            </h1>
          </div>

          <p className="ft-desc">
            Process recharges, transfers, and banking services at enterprise
            speed — secured by multi-layer OTP authentication.
          </p>

          <div className="ft-stats">
            <div className="ft-stat">
              <span className="ft-stat-val">₹2.4B+</span>
              <span className="ft-stat-lbl">Processed</span>
            </div>
            <div className="ft-stat">
              <span className="ft-stat-val">99.9%</span>
              <span className="ft-stat-lbl">Uptime</span>
            </div>
            <div className="ft-stat">
              <span className="ft-stat-val">1.2M+</span>
              <span className="ft-stat-lbl">Retailers</span>
            </div>
          </div>

          <div className="ft-card-visual" aria-hidden="true">
            <div className="ft-card-chip">
              <div className="ft-card-top">
                <span className="ft-card-brand">
                  {APP_CONFIG.ORGANISATION_BRAND || "RETAILPAY"}
                </span>
                <div className="ft-card-chip-icon">
                  <div className="ft-chip-cell" />
                  <div className="ft-chip-cell" />
                  <div className="ft-chip-cell" />
                  <div className="ft-chip-cell" />
                </div>
              </div>
              <div className="ft-card-num">
                <span>••••</span>
                <span>••••</span>
                <span>••••</span>
                <span>4821</span>
              </div>
              <div className="ft-card-bottom">
                <span className="ft-card-holder">Retailer Account</span>
                <span className="ft-card-expiry">12 / 28</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className="ft-right">
          <div className="ft-form-inner">
            <div className="ft-form-brand">
              <img src={APP_CONFIG.ORGANISATION_LOGO} alt="Logo" />
            </div>

            <div className="ft-form-hd">
              <p className="ft-form-pre">Retailer Portal</p>
              <h2 className="ft-form-title">Welcome back</h2>
              <p className="ft-form-sub">
                Sign in to {APP_CONFIG.ORGANISATION_BRAND} to continue
              </p>
            </div>

            {/* General error */}
            {errors.general && (
              <div className="ft-alert ft-alert-danger">
                <span>⚠️</span>
                {errors.general}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Phone field */}
              <div className="ft-field">
                <label className="ft-label" htmlFor="email">
                  Phone Number
                </label>
                <div className="ft-input-wrap">
                  <span className="ft-input-icon">📞</span>
                  <input
                    className={`ft-input${errors.email ? " ft-input-error" : ""}`}
                    id="email"
                    name="email"
                    type="text"
                    placeholder="Enter your 10-digit number"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="tel"
                  />
                  <div className="ft-input-glow" />
                </div>
                {errors.email && (
                  <p className="ft-error-msg">
                    <span>⚠</span>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password field */}
              <div className="ft-field">
                <label className="ft-label" htmlFor="password">
                  Password
                </label>
                <div className="ft-input-wrap">
                  <span className="ft-input-icon">🔒</span>
                  <input
                    className={`ft-input${errors.password ? " ft-input-error" : ""}`}
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <div className="ft-input-glow" />
                  <button
                    type="button"
                    className="ft-eye-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                </div>
                {errors.password && (
                  <p className="ft-error-msg">
                    <span>⚠</span>
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Remember me + forgot */}
              <div className="ft-opts-row">
                <label className="ft-remember">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span className="ft-remember-label">Remember me</span>
                </label>
                <a className="ft-forgot" href="/forgot_password">
                  Forgot Password?
                </a>
              </div>

              {/* Submit */}
              <button className="ft-btn" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                      style={{
                        width: "1rem",
                        height: "1rem",
                        borderWidth: "2px",
                      }}
                    />
                    Signing in…
                  </>
                ) : (
                  <>Sign In →</>
                )}
              </button>
            </form>

            <div className="ft-divider">
              <div className="ft-divider-line" />
              <span className="ft-divider-text">NEW HERE?</span>
              <div className="ft-divider-line" />
            </div>

            <div className="ft-signup-row">
              Don't have an account?{" "}
              <a href="/register">Create one free</a>
            </div>

            <div className="ft-secure-badge">
              <div className="ft-secure-dot" aria-hidden="true" />
              <span>256-BIT SSL · OTP SECURED · PCI DSS</span>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <Ticker />
      </div>
    </>
  );
};

export default SignIn;